type AnalyticsProperties = Record<string, string | number | boolean | null | undefined>;

const STATS_BASE_URL = 'https://log.mxfast.com';
const PRODUCT = 'uumail';
const EXPERIMENT_ID = 'entry_positioning';
const VARIANT_KEY = 'uumail_entry_positioning_variant';
const ENTRY_SOURCE_KEY = 'uumail_entry_source';
const DEVICE_ID_KEY = 'uumail_stats_device_id';
const SESSION_ID_KEY = 'uumail_stats_session_id';
const PENDING_LOGIN_KEY = 'uumail_stats_pending_login';
const ALIAS_CREATE_OPEN_DEDUPE_MS = 30_000;

type EntrySource = 'landing' | 'sidebar' | 'dashboard';
type Variant = 'privacy_first' | 'fast_usable_email';
const ACTIVE_VARIANT: Variant = 'fast_usable_email';

const createId = (prefix: string) => {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
  return `${prefix}-${random}`;
};

const readLocalStorage = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeLocalStorage = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Analytics must never break product flows.
  }
};

const readSessionStorage = (key: string) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionStorage = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Analytics must never break product flows.
  }
};

export const getExperimentVariant = (): Variant => {
  writeLocalStorage(VARIANT_KEY, ACTIVE_VARIANT);
  return ACTIVE_VARIANT;
};

export const createAnalyticsFlowId = (prefix: string) => createId(prefix);

export const initializeAnalyticsEntry = (entrySource: EntrySource) => {
  writeSessionStorage(ENTRY_SOURCE_KEY, entrySource);
};

const inferEntrySource = (): EntrySource => {
  const existing = readSessionStorage(ENTRY_SOURCE_KEY);
  if (existing === 'landing' || existing === 'sidebar' || existing === 'dashboard') {
    return existing;
  }

  if (window.location.pathname === '/') return 'landing';
  if (window.location.pathname.includes('sidebar')) return 'sidebar';
  return 'dashboard';
};

const getOrCreateDeviceId = () => {
  const existing = readLocalStorage(DEVICE_ID_KEY);
  if (existing) return existing;

  const deviceId = createId('device');
  writeLocalStorage(DEVICE_ID_KEY, deviceId);
  return deviceId;
};

const getOrCreateSessionId = () => {
  const existing = readSessionStorage(SESSION_ID_KEY);
  if (existing) return existing;

  const sessionId = createId('session');
  writeSessionStorage(SESSION_ID_KEY, sessionId);
  return sessionId;
};

export const getAnalyticsSessionId = () => getOrCreateSessionId();

export const trackLoginStart = () => {
  writeSessionStorage(PENDING_LOGIN_KEY, '1');
  trackProductEvent('login_start');
};

export const trackLoginSuccess = (userId: number | undefined) => {
  if (!readSessionStorage(PENDING_LOGIN_KEY)) return;
  window.sessionStorage.removeItem(PENDING_LOGIN_KEY);
  trackProductEvent('login_success', { userId });
};

const inferSubProduct = (entrySource: EntrySource) => (
  entrySource === 'sidebar' ? 'sidebar' : 'web'
);

const recentAliasCreateOpenEvents = new Map<string, number>();

const shouldSkipDuplicateAliasCreateOpen = (
  eventName: string,
  entrySource: EntrySource,
  sessionId: string,
  userId: number | undefined,
  properties: AnalyticsProperties,
) => {
  if (eventName !== 'alias_create_open') return false;

  const source = typeof properties.source === 'string' ? properties.source : '';
  const key = [
    entrySource,
    sessionId,
    source,
    userId ?? 'anonymous',
  ].join('|');
  const now = Date.now();
  const lastSeenAt = recentAliasCreateOpenEvents.get(key);

  if (lastSeenAt && now - lastSeenAt < ALIAS_CREATE_OPEN_DEDUPE_MS) {
    return true;
  }

  recentAliasCreateOpenEvents.set(key, now);
  return false;
};

export const trackProductEvent = (eventName: string, properties: AnalyticsProperties = {}) => {
  const entrySource = inferEntrySource();
  const variant = getExperimentVariant();
  const { userId, flowId, ...eventProperties } = properties;
  const numericUserId = typeof userId === 'number'
    ? userId
    : (typeof userId === 'string' && /^\d+$/.test(userId) ? Number(userId) : undefined);
  const normalizedFlowId = typeof flowId === 'string' && flowId ? flowId : undefined;
  const deviceId = getOrCreateDeviceId();
  const sessionId = getOrCreateSessionId();

  if (shouldSkipDuplicateAliasCreateOpen(eventName, entrySource, sessionId, numericUserId, eventProperties)) {
    return;
  }

  fetch(`${STATS_BASE_URL}/stats/${PRODUCT}/event`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({
      event_name: eventName,
      sub_product: inferSubProduct(entrySource),
      user_id: numericUserId,
      flow_id: normalizedFlowId,
      device_id: deviceId,
      session_id: sessionId,
      experiment_id: EXPERIMENT_ID,
      variant,
      properties: {
        path: window.location.pathname,
        entry_source: entrySource,
        ...eventProperties,
      },
    }),
  }).catch(() => {});
};
